import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Fab,
  Tooltip,
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  Slide,
  Divider,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from './config';
import MessageRenderer, { findExportableProductIds, handleProductIdsExport } from './MessageRenderer';

const EXPORT_REGEX = /\b(export|download|save)\b/i;
const EXPORT_IMAGES_REGEX = /\b(image|images|photo|photos|picture|pictures|zip)\b/i;

const PRIMARY = '#1d5576';
const ACCENT = '#f9a91e';

const INITIAL_MESSAGE = {
  role: 'assistant',
  content: "Hi! I'm your Gibson Sales Portal assistant. How can I help you today?",
};

function formatSessionDate(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString();
}

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('chat'); // 'chat' | 'history'
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [modelName, setModelName] = useState('');
  const [sessionId, setSessionId] = useState(null);

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const bottomRef = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('access');

  useEffect(() => {
    if (open && view === 'chat') bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, open, view]);

  // Fetch session list
  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/chat/sessions/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSessions(await res.json());
    } catch {
      // ignore
    } finally {
      setSessionsLoading(false);
    }
  }, [token]);

  // Load sessions when history view opens
  useEffect(() => {
    if (view === 'history') fetchSessions();
  }, [view, fetchSessions]);

  // Load a saved session into the chat view
  const loadSession = async (sid) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/chat/sessions/${sid}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const loaded = data.messages.map((m) => ({
          role: m.role,
          content: m.content,
          table_data: m.table_data || null,
          product_cards: m.product_cards || null,
        }));
        setMessages(loaded.length > 0 ? loaded : [INITIAL_MESSAGE]);
        setSessionId(sid);
        setView('chat');
      }
    } catch {
      // ignore
    }
  };

  const startNewChat = () => {
    setMessages([INITIAL_MESSAGE]);
    setSessionId(null);
    setInput('');
    setView('chat');
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    // Intercept export intent: only act when real, exportable products were actually
    // shown earlier in the conversation — otherwise fall through to the normal chat
    // flow, which correctly explains there's nothing to export yet.
    if (EXPORT_REGEX.test(text)) {
      const productIds = findExportableProductIds(messages);
      if (productIds) {
        const wantsImages = EXPORT_IMAGES_REGEX.test(text);
        setMessages((prev) => [
          ...prev,
          { role: 'user', content: text },
          {
            role: 'assistant',
            content: wantsImages
              ? 'Downloading the images as a ZIP file — your download should start shortly.'
              : 'Exporting to Excel — your download should start shortly.',
          },
        ]);
        setInput('');
        if (wantsImages) {
          handleProductIdsExport('/api/download-product-images/', productIds, `product_images_${productIds.length}.zip`);
        } else {
          handleProductIdsExport('/api/export-products/', productIds, `products_${productIds.length}.xlsx`);
        }
        return;
      }
    }

    const userMsg = { role: 'user', content: text };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    const history = updatedMessages.slice(1, -1);

    try {
      const res = await fetch(`${API_BASE_URL}/api/chat/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text, history, session_id: sessionId, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
      });
      const data = await res.json();

      if (res.status === 401) {
        setMessages((prev) => [...prev, { role: 'assistant', content: 'Your session has expired. Redirecting you to the login page…' }]);
        setTimeout(() => {
          localStorage.clear();
          sessionStorage.clear();
          navigate('/');
        }, 1500);
        return;
      }
      if (!res.ok) {
        setMessages((prev) => [...prev, { role: 'assistant', content: data.error || data.detail || 'Something went wrong. Please try again.' }]);
        return;
      }

      if (data.model && !modelName) setModelName(data.model);
      if (data.session_id && !sessionId) setSessionId(data.session_id);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.reply || data.error || 'No response received.',
          table_data: data.table_data || null,
          product_cards: data.product_cards || null,
        },
      ]);
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearChat = () => { setMessages([INITIAL_MESSAGE]); setSessionId(null); };

  const openFullScreen = () => { setOpen(false); navigate('/ai-assistant'); };

  if (!token) return null;

  const savedSessions = sessions.filter((s) => s.is_saved);
  const recentSessions = sessions.filter((s) => !s.is_saved);

  return (
    <>
      {/* Chat / History Panel */}
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 90,
            right: 24,
            width: { xs: 'calc(100vw - 48px)', sm: 480 },
            height: { xs: 560, sm: 620 },
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '16px',
            overflow: 'hidden',
            zIndex: 1300,
          }}
        >
          {/* Header */}
          <Box sx={{ bgcolor: PRIMARY, px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {view === 'history' ? (
                <Tooltip title="Back to chat">
                  <IconButton size="small" onClick={() => setView('chat')} sx={{ color: 'rgba(255,255,255,0.8)', mr: 0.5 }}>
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <SmartToyIcon sx={{ color: ACCENT, fontSize: 22 }} />
              )}
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
                  {view === 'history' ? 'Chat History' : 'Gibson AI'}
                </Typography>
                {view === 'chat' && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>
                    Powered By Oracle GenAI
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {view === 'history' ? (
                <Tooltip title="New chat">
                  <IconButton size="small" onClick={startNewChat} sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <>
                  <Tooltip title="Chat history">
                    <IconButton size="small" onClick={() => setView('history')} sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Full screen">
                    <IconButton size="small" onClick={openFullScreen} sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      <OpenInFullIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Clear chat">
                    <IconButton size="small" onClick={clearChat} sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <Tooltip title="Close">
                <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Divider />

          {/* History view */}
          {view === 'history' && (
            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
              {sessionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress size={24} sx={{ color: PRIMARY }} />
                </Box>
              ) : sessions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', mt: 2 }}>
                  No chat history yet.
                </Typography>
              ) : (
                <>
                  {savedSessions.length > 0 && (
                    <>
                      <Typography variant="caption" sx={{ px: 2, pt: 1.5, pb: 0.25, display: 'block', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.05em' }}>
                        Saved
                      </Typography>
                      <List dense disablePadding>
                        {savedSessions.map((s) => (
                          <WidgetSessionItem key={s.id} session={s} active={sessionId === s.id} onSelect={() => loadSession(s.id)} />
                        ))}
                      </List>
                      <Divider sx={{ my: 0.5 }} />
                    </>
                  )}
                  {recentSessions.length > 0 && (
                    <>
                      <Typography variant="caption" sx={{ px: 2, pt: 1, pb: 0.25, display: 'block', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.05em' }}>
                        Recent
                      </Typography>
                      <List dense disablePadding>
                        {recentSessions.map((s) => (
                          <WidgetSessionItem key={s.id} session={s} active={sessionId === s.id} onSelect={() => loadSession(s.id)} />
                        ))}
                      </List>
                    </>
                  )}
                </>
              )}
            </Box>
          )}

          {/* Chat view */}
          {view === 'chat' && (
            <>
              <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {messages.map((msg, idx) => {
                  const hasTable = msg.role === 'assistant' && (msg.content.includes('|') || msg.product_cards?.length > 0);
                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                        alignItems: 'flex-start',
                        gap: 1,
                        width: hasTable ? '100%' : undefined,
                      }}
                    >
                      <Avatar sx={{ bgcolor: msg.role === 'user' ? ACCENT : PRIMARY, width: 28, height: 28, flexShrink: 0 }}>
                        {msg.role === 'user' ? <PersonIcon sx={{ fontSize: 15 }} /> : <SmartToyIcon sx={{ fontSize: 15 }} />}
                      </Avatar>
                      <Box
                        sx={{
                          px: 1.5, py: 1,
                          maxWidth: hasTable ? 'calc(100% - 40px)' : '82%',
                          minWidth: hasTable ? 'calc(100% - 80px)' : undefined,
                          bgcolor: msg.role === 'user' ? `${PRIMARY}12` : '#f5f5f5',
                          border: `1px solid ${msg.role === 'user' ? `${PRIMARY}30` : '#e0e0e0'}`,
                          borderRadius: msg.role === 'user' ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
                          overflow: 'hidden',
                        }}
                      >
                        <MessageRenderer content={msg.content} compact={true} tableData={msg.table_data} productCards={msg.product_cards} />
                      </Box>
                    </Box>
                  );
                })}

                {loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: PRIMARY, width: 28, height: 28 }}>
                      <SmartToyIcon sx={{ fontSize: 15 }} />
                    </Avatar>
                    <Box sx={{ px: 1.5, py: 1, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '4px 12px 12px 12px', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={12} thickness={5} sx={{ color: PRIMARY }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>Thinking...</Typography>
                    </Box>
                  </Box>
                )}

                <div ref={bottomRef} />
              </Box>

              <Box sx={{ p: 1.5, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                  fullWidth multiline maxRows={3} size="small"
                  placeholder="Ask me anything..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.85rem', '&.Mui-focused fieldset': { borderColor: PRIMARY } } }}
                />
                <IconButton
                  onClick={sendMessage}
                  disabled={!input.trim() || loading}
                  size="small"
                  sx={{ bgcolor: PRIMARY, color: 'white', width: 38, height: 38, borderRadius: '10px', flexShrink: 0, '&:hover': { bgcolor: '#164060' }, '&.Mui-disabled': { bgcolor: '#ccc', color: 'white' } }}
                >
                  <SendIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </>
          )}
        </Paper>
      </Slide>

      {/* FAB Toggle */}
      <Tooltip title={open ? 'Close assistant' : 'Gibson AI'} placement="left">
        <Fab
          onClick={() => setOpen((prev) => !prev)}
          sx={{
            position: 'fixed', bottom: 24, right: 24,
            bgcolor: open ? '#164060' : PRIMARY,
            color: 'white', zIndex: 1300,
            '&:hover': { bgcolor: '#164060' },
            boxShadow: '0 4px 20px rgba(29,85,118,0.4)',
          }}
        >
          {open ? <CloseIcon /> : <SmartToyIcon />}
        </Fab>
      </Tooltip>
    </>
  );
};

// Session row for the history list
const WidgetSessionItem = ({ session, active, onSelect }) => (
  <ListItemButton
    selected={active}
    onClick={onSelect}
    sx={{
      py: 0.75, px: 2,
      '&.Mui-selected': { bgcolor: `${PRIMARY}15` },
    }}
  >
    <ListItemText
      primary={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {session.is_saved && <BookmarkIcon sx={{ fontSize: 13, color: PRIMARY, flexShrink: 0 }} />}
          <Typography variant="body2" noWrap sx={{ fontSize: '0.82rem', fontWeight: active ? 600 : 400 }}>
            {session.title || 'Untitled'}
          </Typography>
        </Box>
      }
      secondary={
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
          {formatSessionDate(session.updated_at)}
        </Typography>
      }
    />
  </ListItemButton>
);

export default ChatWidget;
